import { getToolById, getCategoryDisplayLabel } from '../data/tools';
import { useChat } from '../context/ChatContext';
import { useToolbox } from '../context/ToolboxContext';
import { useTabs } from '../context/TabContext';
import { HipaaBadge } from './HipaaBadge';

export function SuggestedToolsTab() {
  const { suggestedToolIds, newToolIds, clearSuggestedTools } = useChat();
  const { isToolInToolbox, addTool } = useToolbox();
  const { openToolTab } = useTabs();

  // Get unique tools (in case of duplicates)
  const suggestedTools = suggestedToolIds
    .map(id => getToolById(id))
    .filter((tool): tool is NonNullable<typeof tool> => tool !== undefined);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-neutral-800 font-heading">Suggested Tools</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              {suggestedTools.length} {suggestedTools.length === 1 ? 'tool' : 'tools'} recommended based on your conversation
            </p>
          </div>
          {suggestedTools.length > 0 && (
            <button
              onClick={clearSuggestedTools}
              className="btn-ghost text-neutral-600 hover:text-red-600 hover:bg-red-50"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Tools List - scrollable */}
      <div className={`flex-1 p-6 ${suggestedTools.length === 0 ? 'overflow-y-hidden' : 'overflow-y-auto overscroll-contain'}`}>
        {suggestedTools.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-4">
            {suggestedTools.map((tool, index) => {
              // Show divider after the last "new" tool if there are also older tools
              const isNew = newToolIds.has(tool.id);
              const nextTool = suggestedTools[index + 1];
              const showDivider = isNew && nextTool && !newToolIds.has(nextTool.id);

              return (
              <div key={tool.id}>
              <div className={`card border-l-2 ${isNew ? 'border-l-primary-500' : 'border-l-accent-400'} p-4`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-neutral-800 font-heading">{tool.name}</h4>
                      <HipaaBadge compliant={tool.hipaaCompliant} />
                      <span className="text-xs font-medium text-neutral-600 bg-neutral-100 px-2 py-0.5 rounded-pill">
                        {getCategoryDisplayLabel(tool)}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-600 line-clamp-2">{tool.description}</p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openToolTab(tool.id, tool.name)}
                      className="btn-primary flex-1 text-center"
                    >
                      {tool.id === 'freed-ai' || tool.id === 'doximity-scribe' || tool.id === 'intakeq'
                        ? 'View Setup Guide'
                        : 'Learn More'}
                    </button>
                    <a
                      href={tool.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary flex items-center gap-1"
                    >
                      <span>Website</span>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                  {/* Add to Toolbox button */}
                  <button
                    onClick={() => addTool(tool)}
                    disabled={isToolInToolbox(tool.id)}
                    className={`w-full px-3 py-2 text-sm font-medium rounded-btn transition-colors flex items-center justify-center gap-1.5 ${
                      isToolInToolbox(tool.id)
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 border border-neutral-200'
                    }`}
                  >
                    {isToolInToolbox(tool.id) ? (
                      <>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Added to Toolbox
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add To Toolbox
                      </>
                    )}
                  </button>
                </div>
              </div>
              {showDivider && (
                <div className="flex items-center gap-3 py-1">
                  <div className="flex-1 h-px bg-neutral-200" />
                  <span className="text-xs text-neutral-400">earlier suggestions</span>
                  <div className="flex-1 h-px bg-neutral-200" />
                </div>
              )}
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 py-12">
      <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-neutral-800 mb-2 font-heading">No Tools Suggested Yet</h3>
      <p className="text-sm text-neutral-500 max-w-sm">
        Start a conversation with the AI assistant to get personalized tool recommendations. Suggested tools will appear here automatically.
      </p>
    </div>
  );
}
